// ADR 5.9.26r — the flashcard routes, and the wire shapes they speak.
//
// Every type here is the backend's JSON verbatim (snake_case,
// `HSKVocabBackend/crates/core/src/models/flashcard/`), for the reason
// `plans.ts` gives: the one place the web reshapes a card is the review
// reducer's `ReviewCard`, and a second renaming layer here would be a second
// place for the two to drift.
//
// `interval_days` on the review response is the field this module exists to
// carry. SM-2 lives in `Backend crates/core/src/services/srs.rs::compute_sm2`,
// `POST /review` answers with the recomputed card, and that number is the
// schedule — nothing in `web/` derives one (ADR 5.9.26r §2). There are no SRS
// constants in this file and there must never be.
//
// `/activity/log` sits here rather than in an `activity.ts` of its own because
// the review loop is its only web caller and the reducer already emits its
// payload; a second caller is what would earn it a module.

import { authed, authedJson } from './http'

/** `GET /flashcards`, `POST /review` and the rows inside `/due` (`FlashcardResponse`). */
export interface Flashcard {
  id: string
  front: string
  back: string
  source_lang: string
  target_lang: string
  level?: number | null
  tags: string[]
  list_id?: string | null
  ease_factor: number
  /** The server's schedule for this card. Read, never computed (§2). */
  interval_days: number
  repetitions: number
  next_review_at: string
  created_at: string
  is_pinned: boolean
  /** ADR 16.8.26a's raw timestamps — a badge renders the date, not a boolean. */
  snoozed_until?: string | null
  suspended_at?: string | null
}

/** `GET /flashcards/due`. The pinned aggregates are 0 without a `list_id`. */
export interface DueResponse {
  cards: Flashcard[]
  total_due: number
  pinned_due: number
  pinned_count: number
}

/** `GET /flashcard-lists` rows, and `/flashcard-lists/pinned` (`ListResponse`). */
export interface FlashcardList {
  id: string
  name: string
  card_count: number
  due_count: number
  created_at: string
  is_pinned: boolean
  pinned_card_count: number
  pinned_due_count: number
}

/** The backend's `CursorPage<T>` — `total` only on a first page. */
export interface CursorPage<T> {
  items: T[]
  next_cursor?: string | null
  has_more: boolean
  total?: number
}

/**
 * The `PUT /flashcards/{id}/snooze` body (ADR 16.8.26a D2).
 *
 * Flat on the wire, discriminated here: `days` exists only on the `days` arm,
 * so "indefinite with days = 3" cannot be spelled. The server validates
 * `{1, 3, 5}` independently — this type is a convenience, never the boundary.
 */
export type SnoozeBody =
  | { mode: 'days'; days: number }
  | { mode: 'indefinite' }
  | { mode: 'clear' }

export interface SnoozeResponse {
  id: string
  snoozed_until?: string | null
  suspended_at?: string | null
}

/** `POST /activity/log`. `items_completed` below 1 is a 400, so the reducer gates it. */
export interface LogActivityBody {
  activity_type: string
  items_completed: number
}

/**
 * ADR 10.9.26h §6 — `CreateFlashcardRequest` (`models/flashcard/mod.rs:36`),
 * the body of the web's first `POST /flashcards`.
 *
 * `tags` carries the phones' one provenance tag (`word` / `sentence`,
 * `SaveToListPicker.kt`), and `list_id: null` files the card in no list —
 * spelled rather than omitted so the body says on its face that the picker
 * was answered with "none", not skipped.
 */
export interface CreateFlashcardBody {
  front: string
  back: string
  source_lang: string
  target_lang: string
  level: number | null
  tags: string[]
  list_id: string | null
}

/** `POST /flashcards` — answers with the card the server created. */
export function createFlashcard(body: CreateFlashcardBody): Promise<Flashcard> {
  return authedJson<Flashcard>('/flashcards', { method: 'POST', body })
}

/**
 * `GET /flashcards/due` — one page, at the server's default limit of 20.
 *
 * No `list_id` and no `limit`: this release reviews everything and the session
 * is that one page (§3). Passing a limit would only be the client choosing a
 * number the server already has an opinion about.
 */
export function dueCards(): Promise<DueResponse> {
  return authedJson<DueResponse>('/flashcards/due')
}

export function listCards(listId: string): Promise<CursorPage<Flashcard>> {
  return authedJson<CursorPage<Flashcard>>(
    `/flashcards?list_id=${encodeURIComponent(listId)}`,
  )
}

/**
 * `POST /flashcards/{id}/review` — answers with the card the server rescheduled.
 *
 * ADR 23.9.26w §3: `opId` is minted before the first attempt and sent as
 * `Idempotency-Key` on it and on every replay, so a review whose response was
 * lost is served from the Backend's guard rather than scheduled twice.
 */
export function reviewCard(id: string, quality: number, opId: string): Promise<Flashcard> {
  return authedJson<Flashcard>(`/flashcards/${id}/review`, {
    method: 'POST',
    body: { quality },
    headers: { 'Idempotency-Key': opId },
  })
}

export function snoozeCard(id: string, body: SnoozeBody): Promise<SnoozeResponse> {
  return authedJson<SnoozeResponse>(`/flashcards/${id}/snooze`, { method: 'PUT', body })
}

export async function pinCard(id: string, pinned: boolean): Promise<void> {
  await authed(`/flashcards/${id}/pin`, { method: 'PUT', body: { pinned } })
}

/** `PUT /flashcards/{id}/move` — `null` moves the card out of every list. */
export async function moveCard(id: string, listId: string | null): Promise<void> {
  await authed(`/flashcards/${id}/move`, { method: 'PUT', body: { list_id: listId } })
}

export async function deleteCard(id: string): Promise<void> {
  await authed(`/flashcards/${id}`, { method: 'DELETE' })
}

export function listLists(): Promise<CursorPage<FlashcardList>> {
  return authedJson<CursorPage<FlashcardList>>('/flashcard-lists')
}

/**
 * ADR 30.9.26f §6 — the search query string. No floor here: the wrappers send
 * what they are given, and `meetsSearchFloor` is the hook's.
 */
function searchQuery(q: string, limit?: number): string {
  const params = new URLSearchParams({ q })
  if (limit !== undefined) params.set('limit', String(limit))
  return params.toString()
}

/** `GET /flashcard-lists?q=&limit=` — ADR 30.6.26g's subsequence name search. */
export function searchLists(q: string, limit?: number): Promise<CursorPage<FlashcardList>> {
  return authedJson<CursorPage<FlashcardList>>(`/flashcard-lists?${searchQuery(q, limit)}`)
}

/** `GET /flashcards?q=&limit=` — ADR 30.6.26h's subsequence front/back search. */
export function searchCards(q: string, limit?: number): Promise<CursorPage<Flashcard>> {
  return authedJson<CursorPage<Flashcard>>(`/flashcards?${searchQuery(q, limit)}`)
}

/** `GET /flashcard-lists/pinned` — a bare array, newest pin first (§5). */
export function pinnedLists(): Promise<FlashcardList[]> {
  return authedJson<FlashcardList[]>('/flashcard-lists/pinned')
}

export function createList(name: string): Promise<FlashcardList> {
  return authedJson<FlashcardList>('/flashcard-lists', { method: 'POST', body: { name } })
}

export async function renameList(id: string, name: string): Promise<void> {
  await authed(`/flashcard-lists/${id}`, { method: 'PUT', body: { name } })
}

export async function deleteList(id: string): Promise<void> {
  await authed(`/flashcard-lists/${id}`, { method: 'DELETE' })
}

export async function pinList(id: string, pinned: boolean): Promise<void> {
  await authed(`/flashcard-lists/${id}/pin`, { method: 'PUT', body: { pinned } })
}

export async function logActivity(body: LogActivityBody): Promise<void> {
  await authed('/activity/log', { method: 'POST', body })
}
